import { Button } from "@heroui/react";
import { useCallback, useState } from "react";
import { InvitationResponse, ExistingRenterAcceptCallback } from "./types";
import { InvitationInfoCard } from "./InvitationInfoCard";
import { InvitationFlowWrapper } from "./InvitationFlowWrapper";
import { StepHeader } from "./StepHeader";

interface ExistingRenterFlowProps {
  invitationData: InvitationResponse;
  onAccept: ExistingRenterAcceptCallback;
  isSubmitting?: boolean;
}

export function ExistingRenterFlow({
  invitationData,
  onAccept,
  isSubmitting = false,
}: ExistingRenterFlowProps) {
  // opaqueId ранее использовался для localStorage; больше не нужен
  const [activeStep, setActiveStep] = useState(0);
  const { agreement } = invitationData;

  const steps = [
    {
      name: "Информация о приглашении",
      description: "Ознакомьтесь с деталями приглашения",
      longDescription:
        "Вам поступило приглашение стать арендатором офиса. Пожалуйста, ознакомьтесь с информацией о приглашении.",
    },
    {
      name: "Принятие приглашения",
      description: "Подтвердите принятие приглашения",
      longDescription:
        "Подтвердите принятие приглашения для завершения процесса.",
      hideTitle: true,
    },
  ];

  //

  const changeStep = useCallback(
    (step: number) => {
      // Разрешаем переход только на уже пройденные шаги (назад) или текущий
      if (step > activeStep) return;
      if (step === activeStep) return;
      setActiveStep(step);
    },
    [activeStep],
  );

  const handleAccept = useCallback(() => {
    onAccept();
  }, [onAccept]);

  // Определяем завершенные шаги для отображения в степпере
  const completedSteps = [];
  for (let i = 0; i < activeStep; i++) {
    completedSteps.push(i);
  }

  return (
    <InvitationFlowWrapper
      steps={steps}
      activeStep={activeStep}
      changeStep={changeStep}
      completedSteps={completedSteps}
      title="Приглашение в Elasticspace"
      subtitle={`${agreement.business_profile_name || "Бизнес-профиль"}: Принятие приглашения`}>
      <div className="flex flex-col gap-4">
        <StepHeader step={steps[activeStep]} />

        {activeStep === 0 && (
          <div className="w-full flex flex-col gap-6">
            <InvitationInfoCard agreement={agreement} />
            <div className="flex justify-end">
              <Button
                className="bg-elastic-500 hover:bg-elastic-600 text-black font-medium"
                onClick={() => setActiveStep(1)}>
                Продолжить
              </Button>
            </div>
          </div>
        )}

        {activeStep === 1 && (
          <div className="w-full flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl font-bold text-foreground">
                Принятие приглашения
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Вы подтверждаете принятие приглашения стать арендатором офиса "
                {agreement.office_name}" от бизнес-профиля "
                {agreement.business_profile_name}".
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-end">
              <Button variant="light" onPress={() => setActiveStep(0)}>
                Назад
              </Button>
              <Button
                className="bg-elastic-500 hover:bg-elastic-600 text-black font-medium"
                onClick={handleAccept}
                isLoading={isSubmitting}
                isDisabled={isSubmitting}>
                Принять приглашение
              </Button>
            </div>
          </div>
        )}
      </div>
    </InvitationFlowWrapper>
  );
}
